/**
 * CSV Export utilities for leads
 */

import type { Lead } from "../Lead.types";

/**
 * Fields to include in CSV export, in order
 */
const CSV_FIELDS: Array<{ key: keyof Lead; header: string }> = [
  { key: "name", header: "Name" },
  { key: "status", header: "Status" },
  { key: "sector", header: "Sector" },
  { key: "city", header: "City" },
  { key: "country", header: "Country" },
  { key: "address", header: "Address" },
  { key: "phone", header: "Phone" },
  { key: "emailAddress", header: "Email" },
  { key: "website", header: "Website" },
  { key: "description", header: "Description" },
  { key: "instagramUrl", header: "Instagram URL" },
  { key: "instagramFollowers", header: "Instagram Followers" },
  { key: "instagramPosts", header: "Instagram Posts" },
  { key: "googleUrl", header: "Google URL" },
  { key: "googleRating", header: "Google Rating" },
  { key: "googleReviews", header: "Google Reviews" },
  { key: "score", header: "Score" },
  { key: "contactAttempts", header: "Contact Attempts" },
  { key: "lastContactedAt", header: "Last Contacted" },
  { key: "createdAt", header: "Created At" },
];

/**
 * Escape a CSV field value
 * - Wrap in quotes if contains comma, quote, or newline
 * - Double any quote characters
 */
function escapeCSVField(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }

  const stringValue = String(value);

  // Check if we need to escape
  if (
    stringValue.includes(",") ||
    stringValue.includes('"') ||
    stringValue.includes("\n") ||
    stringValue.includes("\r")
  ) {
    // Escape quotes by doubling them, wrap in quotes
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
}

/**
 * Convert an array of leads to CSV string
 */
export function leadsToCSV(leads: Lead[]): string {
  // Header row
  const headerRow = CSV_FIELDS.map((f) => f.header).join(",");

  // Data rows
  const dataRows = leads.map((lead) => {
    return CSV_FIELDS.map((field) => {
      const value = lead[field.key];
      return escapeCSVField(value);
    }).join(",");
  });

  return [headerRow, ...dataRows].join("\n");
}

/**
 * Trigger browser download of CSV content
 */
export function downloadCSV(content: string, filename: string): void {
  // Add BOM for Excel UTF-8 compatibility
  const BOM = "\uFEFF";
  const blob = new Blob([BOM + content], { type: "text/csv;charset=utf-8;" });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;

  // Append to body, click, and clean up
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Revoke the blob URL after a short delay
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

/**
 * Generate a filename for the CSV export
 */
export function generateExportFilename(statusFilter?: string): string {
  const date = new Date().toISOString().split("T")[0];
  const status = statusFilter && statusFilter !== "ALL"
    ? statusFilter.toLowerCase()
    : "all";
  return `leads_${status}_${date}.csv`;
}
